import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { Formation } from '@sorare/gameplay/src/components/MatchView/Formation';
import { Overview } from '@sorare/gameplay/src/components/MatchView/Overview';
import { PlayerGameScoreDrawer } from '@sorare/gameplay/src/components/stats/PlayerGameScoreDrawer';

import type {
  Last11Query,
  Last11QueryVariables,
} from './__generated__/index.graphql.ts';

const LAST_11_QUERY = gql`
  query Last11Query($id: ID!) {
    football {
      game(id: $id) {
        id
        ...Formation_game
        ...Overview_game
      }
    }
  }
  ${Formation.fragments.game}
  ${Overview.fragments.game}
` as TypedDocumentNode<Last11Query, Last11QueryVariables>;

const FormationContainer = styled.div`
  position: relative;
  background-color: var(--c-nd-100);
  padding: var(--double-unit) 0 var(--triple-unit);
  border-bottom-left-radius: var(--double-unit);
  border-bottom-right-radius: var(--double-unit);
`;
const FootballFieldWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 400px;
  margin: auto;
`;

const HalfFootballFieldLines = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 360 568"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <clipPath id="cut-off-top">
      <rect x="0" y="150.5" width="100%" height="100%" />
    </clipPath>
    <clipPath id="cut-off-bottom">
      <rect x="0" y="0" width="100%" height="985.5px" />
    </clipPath>

    <path
      d="M48,0 L48,150 312,150 312,0"
      fill="transparent"
      stroke="rgba(var( --c-rgb-white), 0.2)"
      strokeWidth={1}
    />
    <path
      d="M113,0 L113,50 247,50 247,0"
      fill="transparent"
      stroke="rgba(var( --c-rgb-white), 0.2)"
      strokeWidth={1}
    />
    <circle
      cx="180"
      cy="120"
      r="62"
      fill="transparent"
      stroke="rgba(var( --c-rgb-white), 0.2)"
      strokeWidth={1}
      clipPath="url(#cut-off-top)"
    />

    <line
      x1="0"
      y1="568"
      x2="100%"
      y2="568"
      stroke="rgba(var( --c-rgb-white), 0.2)"
    />
    <circle
      cx="180"
      cy="568"
      r="84"
      fill="transparent"
      stroke="rgba(var( --c-rgb-white), 0.2)"
      strokeWidth={1}
    />
  </svg>
);

type Props = { gameId: string; slug: string };

export const Last11 = ({ gameId, slug }: Props) => {
  const { up: isDesktopAndAbove } = useScreenSize('desktop');
  const [scoreId, setScoreId] = useState<string>();
  const { data, loading } = useQuery(LAST_11_QUERY, {
    variables: { id: idFromObject(gameId) },
  });

  if (loading) return <LoadingIndicator />;
  if (!data) return null;

  const { game } = data.football;

  return (
    <FullWidthContainer>
      <Vertical gap={1.5}>
        <HeadlineS as="h2">
          <FormattedMessage defaultMessage="Last starting team" />
        </HeadlineS>
        <div>
          <Overview game={game} desktop={isDesktopAndAbove} />
          <FormationContainer>
            <FootballFieldWrapper>
              <HalfFootballFieldLines />
            </FootballFieldWrapper>
            <Formation
              game={game}
              isHomeFormation={game.homeTeam?.slug === slug}
              onPlayerDetailsClick={(_, id) => setScoreId(id)}
            />
          </FormationContainer>
        </div>
      </Vertical>
      <PlayerGameScoreDrawer
        open={!!scoreId}
        onClose={() => setScoreId(undefined)}
        playerGameScoreId={scoreId}
      />
    </FullWidthContainer>
  );
};
